import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Stepper, StepperContent, StepperActions } from '@/components/ui/stepper';
import { ArrowLeft, ArrowRight, Loader2, School, Rocket, ExternalLink } from 'lucide-react';
import { getSchoolOptions, createSchool, uploadSchoolFile } from '@/api/schools';
import type { SchoolOptions, CreateSchoolData, CreateSchoolResponse } from '@/types/school';
import type { V2DeployRequest } from '@/types/v2-deployment';
import { WizardFormData, initialFormData, WIZARD_STEPS } from './types';
import {
  InfoStep,
  ConfigStep,
  DeploymentStep,
  BrandingStep,
  SummaryStep,
} from './components';
import { V2DeployDialog } from '@/components/schools/V2DeployDialog';

export function SchoolCreatePage() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState<WizardFormData>(initialFormData);
  const [options, setOptions] = useState<SchoolOptions | null>(null);
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [codeValidationError, setCodeValidationError] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isDeploying, setIsDeploying] = useState(false);
  const [creationResult, setCreationResult] = useState<CreateSchoolResponse | null>(null);
  const [creationProgress, setCreationProgress] = useState<{
    database: boolean | null;
    folder: boolean | null;
    informations: boolean | null;
  } | null>(null);
  const [v2DeployDialogOpen, setV2DeployDialogOpen] = useState(false);
  const [v2DeployConfig, setV2DeployConfig] = useState<V2DeployRequest | undefined>(undefined);

  useEffect(() => {
    fetchOptions();
  }, []);

  const fetchOptions = async () => {
    try {
      setLoadingOptions(true);
      const data = await getSchoolOptions();
      setOptions(data);
    } catch (err) {
      console.error('Failed to load options:', err);
    } finally {
      setLoadingOptions(false);
    }
  };

  const refreshOptions = async () => {
    try {
      const data = await getSchoolOptions();
      setOptions(data);
    } catch (err) {
      console.error('Failed to refresh options:', err);
    }
  };

  const handleChange = (data: Partial<WizardFormData>) => {
    setFormData((prev) => ({ ...prev, ...data }));
    // Clear errors for changed fields
    const changedKeys = Object.keys(data);
    setErrors((prev) => {
      const newErrors = { ...prev };
      changedKeys.forEach((key) => delete newErrors[key]);
      return newErrors;
    });
    // Clear code validation error when code changes
    if ('code' in data) {
      setCodeValidationError(null);
    }
  };

  const handleCodeValidation = (isValid: boolean, error?: string) => {
    setCodeValidationError(error || null);
  };

  const validateStep = (step: number): boolean => {
    const newErrors: Record<string, string> = {};

    switch (step) {
      case 0: // Info step
        if (!formData.name.trim()) {
          newErrors.name = 'Le nom est requis';
        }
        if (!formData.code.trim()) {
          newErrors.code = 'Le code est requis';
        } else if (!/^[A-Z0-9]+$/.test(formData.code)) {
          newErrors.code =
            'Le code ne doit contenir que des lettres majuscules et chiffres';
        } else if (codeValidationError) {
          newErrors.code = codeValidationError;
        }
        if (!formData.emailSender.trim()) {
          newErrors.emailSender = "L'email expediteur est requis";
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.emailSender)) {
          newErrors.emailSender = "L'email expediteur n'est pas valide";
        }
        break;

      // Other steps don't have required fields
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      if (currentStep < WIZARD_STEPS.length - 1) {
        setCurrentStep(currentStep + 1);
      }
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleStepClick = (stepIndex: number) => {
    // Only allow going back to previous steps (but not after creation)
    if (stepIndex < currentStep && !creationResult) {
      setCurrentStep(stepIndex);
    }
  };

  // Create school WITHOUT deployment
  const handleCreate = async () => {
    if (!validateStep(currentStep)) return;

    setIsCreating(true);

    try {
      // Prepare create data WITHOUT deployment
      const createData: CreateSchoolData = {
        code: formData.code,
        name: formData.name,
        emailSender: formData.emailSender,
        email: formData.email || undefined,
        phone: formData.phone || undefined,
        address: formData.address || undefined,
        addressComplement: formData.addressComplement || undefined,
        addressHeadoffice: formData.addressHeadoffice || undefined,
        addressHeadofficeComplement: formData.addressHeadofficeComplement || undefined,
        domainSender: formData.domainSender || undefined,
        type: formData.type || undefined,
        support: formData.support || undefined,
        vip: formData.vip,

        networkId: formData.networkId || undefined,
        retailerId: formData.retailerId || undefined,
        technicalContactId: formData.technicalContactId || undefined,
        salesContactId: formData.salesContactId || undefined,
        whiteLabelEntityId: formData.whiteLabelEntityId || undefined,
        isEducationNationale: formData.isEducationNationale,
        rne: formData.rne || undefined,
        codeAcademie: formData.codeAcademie || undefined,
        country: formData.country || 'FR',
        latitude: formData.latitude,
        longitude: formData.longitude,
        // No deployment at creation
        deployV1: false,
        deployV2: false,
      };

      // Create school
      const result = await createSchool(createData);
      setCreationResult(result);

      // Upload files if any
      const schoolCode = result.code;
      const uploadPromises: Promise<void>[] = [];

      if (formData.logoFile) {
        uploadPromises.push(
          uploadSchoolFile(schoolCode, 'logo', formData.logoFile).then(() => {})
        );
      }
      if (formData.logoSigninFile) {
        uploadPromises.push(
          uploadSchoolFile(schoolCode, 'logoSignin', formData.logoSigninFile).then(
            () => {}
          )
        );
      }
      if (formData.faviconFile) {
        uploadPromises.push(
          uploadSchoolFile(schoolCode, 'favicon', formData.faviconFile).then(
            () => {}
          )
        );
      }
      if (formData.coverFile) {
        uploadPromises.push(
          uploadSchoolFile(schoolCode, 'cover', formData.coverFile).then(() => {})
        );
      }

      if (uploadPromises.length > 0) {
        await Promise.all(uploadPromises);
      }

      setIsCreating(false);
      // Move to deployment step
      setCurrentStep(4);
    } catch (err) {
      console.error('Failed to create school:', err);
      setCreationResult(null);
      setIsCreating(false);
      setErrors({
        submit:
          err instanceof Error
            ? err.message
            : "Erreur lors de la création de l'école",
      });
    }
  };

  // Deploy the created school
  const handleDeploy = async () => {
    if (!creationResult) return;

    // Handle V2 deployment via dialog
    if (formData.deployV2.enabled) {
      const config: V2DeployRequest = {
        offerId: formData.deployV2.offerId || undefined,
        optionIds: formData.deployV2.optionIds.length > 0 ? formData.deployV2.optionIds : undefined,
      };
      setV2DeployConfig(config);
      setV2DeployDialogOpen(true);
      return;
    }

    // Handle V1 deployment only (no V2)
    if (formData.deployV1.enabled) {
      setIsDeploying(true);
      setCreationProgress({
        database: null,
        folder: null,
        informations: null,
      });

      try {
        const createData: CreateSchoolData = {
          code: creationResult.code,
          name: formData.name,
          emailSender: formData.emailSender,
          deployV1: true,
          deployV2: false,
          v1Config: {
            status: formData.deployV1.status,
            schoolSourceId: formData.deployV1.schoolSourceId || undefined,
            moduleIds: formData.deployV1.moduleIds,
          },
        };

        const result = await createSchool(createData);
        const v1Workflow = result.workflow.v1?.workflow;
        setCreationProgress({
          database: v1Workflow?.database ?? null,
          folder: v1Workflow?.folder ?? null,
          informations: v1Workflow?.informations ?? null,
        });

        // Update creation result to reflect V1 deployment
        setCreationResult({
          ...creationResult,
          hasV1: true,
          hasV2: false,
        });
      } catch (err) {
        console.error('Failed to deploy school:', err);
        setCreationProgress(null);
        setIsDeploying(false);
        setErrors({
          submit:
            err instanceof Error
              ? err.message
              : "Erreur lors du déploiement de l'école",
        });
      }
    }
  };

  const handleV2DeploySuccess = () => {
    // Update creation result to reflect V2 deployment
    if (creationResult) {
      setCreationResult({
        ...creationResult,
        hasV2: true,
      });
    }
  };

  const handleGoToSchool = () => {
    if (creationResult) {
      navigate(`/schools/${creationResult.code}`);
    }
  };

  const handleGoToSchools = () => {
    navigate('/schools');
  };

  // Steps: 0=Info, 1=Config, 2=Branding, 3=Summary, 4=Deployment
  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <InfoStep
            formData={formData}
            onChange={handleChange}
            options={options}
            errors={errors}
            onCodeValidation={handleCodeValidation}
          />
        );
      case 1:
        return (
          <ConfigStep
            formData={formData}
            onChange={handleChange}
            options={options}
          />
        );
      case 2:
        return (
          <BrandingStep
            formData={formData}
            onChange={handleChange}
            options={options}
            onRefreshOptions={refreshOptions}
          />
        );
      case 3:
        return (
          <SummaryStep
            formData={formData}
            options={options}
            isCreating={isCreating}
          />
        );
      case 4:
        return (
          <DeploymentStep
            formData={formData}
            onChange={handleChange}
            options={options}
            isDeploying={isDeploying}
            creationResult={creationResult}
            creationProgress={creationProgress || undefined}
          />
        );
      default:
        return null;
    }
  };

  if (loadingOptions) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const isSummaryStep = currentStep === 3;
  const isDeploymentStep = currentStep === 4;
  const hasDeploymentSelected = formData.deployV1.enabled || formData.deployV2.enabled;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="space-y-1">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon-sm" asChild>
              <Link to="/schools">
                <ArrowLeft className="h-4 w-4" />
              </Link>
            </Button>
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <School className="h-5 w-5" />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-foreground">
              Nouvelle école
            </h1>
          </div>
        </div>
      </div>

      {/* Error message */}
      {errors.submit && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-red-700">
          {errors.submit}
        </div>
      )}

      {/* Stepper */}
      <Card>
        <CardContent className="pt-6">
          <Stepper
            steps={WIZARD_STEPS}
            currentStep={currentStep}
            onStepClick={handleStepClick}
          />

          <StepperContent>{renderStepContent()}</StepperContent>

          <StepperActions>
            {/* Previous button - hidden on deployment step after creation */}
            {!(isDeploymentStep && creationResult) && (
              <Button
                type="button"
                variant="outline"
                onClick={handlePrevious}
                disabled={currentStep === 0 || isCreating}
              >
                <ArrowLeft className="h-4 w-4" />
                Precedent
              </Button>
            )}

            {/* Summary step actions */}
            {isSummaryStep && !creationResult && (
              <Button
                type="button"
                onClick={handleCreate}
                disabled={isCreating}
              >
                {isCreating ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Creation...
                  </>
                ) : (
                  <>
                    <School className="h-4 w-4" />
                    Créer l'école
                  </>
                )}
              </Button>
            )}

            {/* Deployment step actions */}
            {isDeploymentStep && creationResult && (
              <div className="flex gap-2 ml-auto">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleGoToSchools}
                  disabled={isDeploying}
                >
                  Retour a la liste
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleGoToSchool}
                  disabled={isDeploying}
                >
                  <ExternalLink className="h-4 w-4" />
                  Voir la fiche
                </Button>
                {hasDeploymentSelected && !isDeploying && !creationProgress && (
                  <Button
                    type="button"
                    onClick={handleDeploy}
                  >
                    <Rocket className="h-4 w-4" />
                    Deployer
                  </Button>
                )}
              </div>
            )}

            {/* Normal next button for other steps */}
            {!isSummaryStep && !isDeploymentStep && (
              <Button type="button" onClick={handleNext}>
                Suivant
                <ArrowRight className="h-4 w-4" />
              </Button>
            )}
          </StepperActions>
        </CardContent>
      </Card>

      {/* V2 Deployment Dialog */}
      {creationResult && (
        <V2DeployDialog
          open={v2DeployDialogOpen}
          onOpenChange={setV2DeployDialogOpen}
          schoolCode={creationResult.code}
          schoolName={formData.name}
          deployConfig={v2DeployConfig}
          onSuccess={handleV2DeploySuccess}
        />
      )}
    </div>
  );
}
